import axios, { AxiosResponse } from 'axios';
import {
  Workflow,
  WorkflowExecution,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  ExecuteWorkflowRequest,
  WorkflowValidationResult,
  WorkflowTemplate,
  WorkflowDefinition
} from '../../types/workflow.types';
import { API_CONFIG } from '../../config/apiConfig';

const API_BASE_URL = API_CONFIG.WORKFLOW_SERVICE_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 30000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle auth errors
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  errors?: any[];
}

export class WorkflowService {
  // Workflow CRUD operations
  static async createWorkflow(data: CreateWorkflowRequest): Promise<{ workflow: Workflow; validation: WorkflowValidationResult }> {
    const response: AxiosResponse<ApiResponse<{ workflow: Workflow; validation: WorkflowValidationResult }>> =
      await api.post('/workflows', data);
    return response.data.data;
  }

  static async getWorkflow(id: string): Promise<Workflow> {
    const response: AxiosResponse<ApiResponse<{ workflow: Workflow }>> =
      await api.get(`/workflows/${id}`);
    return response.data.data.workflow;
  }

  static async getUserWorkflows(limit = 50, offset = 0): Promise<{ workflows: Workflow[]; count: number }> {
    const response: AxiosResponse<ApiResponse<{ workflows: Workflow[]; count: number }>> =
      await api.get(`/workflows?limit=${limit}&offset=${offset}`);
    return response.data.data;
  }

  static async updateWorkflow(id: string, data: UpdateWorkflowRequest): Promise<Workflow> {
    const response: AxiosResponse<ApiResponse<{ workflow: Workflow }>> =
      await api.put(`/workflows/${id}`, data);
    return response.data.data.workflow;
  }

  static async deleteWorkflow(id: string): Promise<boolean> {
    const response: AxiosResponse<ApiResponse<{}>> =
      await api.delete(`/workflows/${id}`);
    return response.data.success;
  }

  static async duplicateWorkflow(id: string, newName: string): Promise<Workflow> {
    const response: AxiosResponse<ApiResponse<{ workflow: Workflow }>> =
      await api.post(`/workflows/${id}/duplicate`, { name: newName });
    return response.data.data.workflow;
  }

  static async validateWorkflow(definition: WorkflowDefinition): Promise<WorkflowValidationResult> {
    const response: AxiosResponse<ApiResponse<{ validation: WorkflowValidationResult }>> =
      await api.post('/workflows/validate', { definition });
    return response.data.data.validation;
  }

  // Workflow execution operations
  static async executeWorkflow(id: string, request: ExecuteWorkflowRequest): Promise<WorkflowExecution> {
    const response: AxiosResponse<ApiResponse<{ execution: WorkflowExecution }>> =
      await api.post(`/workflows/${id}/execute`, request);
    return response.data.data.execution;
  }

  static async getExecution(id: string): Promise<WorkflowExecution> {
    const response: AxiosResponse<ApiResponse<{ execution: WorkflowExecution }>> =
      await api.get(`/executions/${id}`);
    return response.data.data.execution;
  }

  static async getUserExecutions(
    limit = 50,
    offset = 0,
    status?: string
  ): Promise<{ executions: WorkflowExecution[]; count: number }> {
    let url = `/executions?limit=${limit}&offset=${offset}`;
    if (status) {
      url += `&status=${status}`;
    }

    const response: AxiosResponse<ApiResponse<{ executions: WorkflowExecution[]; count: number }>> =
      await api.get(url);
    return response.data.data;
  }

  static async getWorkflowExecutions(
    workflowId: string,
    limit = 50,
    offset = 0
  ): Promise<{ executions: WorkflowExecution[]; count: number }> {
    const response: AxiosResponse<ApiResponse<{ executions: WorkflowExecution[]; count: number }>> =
      await api.get(`/executions/workflow/${workflowId}?limit=${limit}&offset=${offset}`);
    return response.data.data;
  }

  static async cancelExecution(id: string): Promise<boolean> {
    const response: AxiosResponse<ApiResponse<{}>> =
      await api.post(`/executions/${id}/cancel`);
    return response.data.success;
  }

  static async getExecutionLog(id: string): Promise<{
    executionLog: any[];
    currentNodeId?: string;
    status: string;
  }> {
    const response: AxiosResponse<ApiResponse<{
      executionLog: any[];
      currentNodeId?: string;
      status: string;
    }>> = await api.get(`/executions/${id}/log`);
    return response.data.data;
  }

  static async retryExecution(id: string): Promise<WorkflowExecution> {
    const response: AxiosResponse<ApiResponse<{ execution: WorkflowExecution }>> =
      await api.post(`/executions/${id}/retry`);
    return response.data.data.execution;
  }

  // Template operations (if you want to add template support later)
  static async getTemplates(): Promise<WorkflowTemplate[]> {
    // This would call a templates endpoint when implemented
    return [];
  }

  static async createFromTemplate(templateId: string, name: string): Promise<Workflow> {
    // This would call a templates endpoint when implemented
    throw new Error('Templates not yet implemented');
  }
}

export default WorkflowService;

// Re-export types for convenience
export type { Workflow, WorkflowExecution, WorkflowDefinition, WorkflowValidationResult } from '../../types/workflow.types';